import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  panelClassName?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  className = "",
  panelClassName,
}) => {
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const idRef = useRef(`tooltip-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    if (!open) return;
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const top = rect.top + rect.height + 8;
    setPos({ left: Math.round(centerX), top: Math.round(top) });

    const handleScrollOrResize = () => {
      const currentRect = el.getBoundingClientRect();
      setPos({
        left: Math.round(currentRect.left + currentRect.width / 2),
        top: Math.round(currentRect.top + currentRect.height + 8),
      });
    };

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !pos) return;
    const id = idRef.current;
    const timer = setTimeout(() => {
      const tooltip = document.getElementById(id);
      if (!tooltip) return;
      const tooltipRect = tooltip.getBoundingClientRect();
      const halfWidth = tooltipRect.width / 2;
      const minLeft = 8 + halfWidth;
      const maxLeft = Math.max(8 + halfWidth, window.innerWidth - 8 - halfWidth);
      const clampedLeft = Math.min(Math.max(pos.left, minLeft), maxLeft);
      if (clampedLeft !== pos.left) {
        setPos({ left: Math.round(clampedLeft), top: pos.top });
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos({
        left: Math.round(rect.left + rect.width / 2),
        top: Math.round(rect.top + rect.height + 8),
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [open]);

  const panelClass =
    panelClassName ??
    "z-50 w-max max-w-xs rounded-md bg-surface-accent px-3 py-2 text-xs text-content-inverse shadow-floating";

  return (
    <>
      <span
        ref={wrapperRef}
        className={`inline-block ${className}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        tabIndex={0}
        role="button"
        aria-expanded={open}
        aria-describedby={idRef.current}
      >
        {children}
      </span>
      {open &&
        pos &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            id={idRef.current}
            style={{
              left: pos.left,
              top: pos.top,
              position: "absolute",
              transform: "translateX(-50%)",
            }}
            className={panelClass}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
